type LinkMark = { type: string; attrs?: Record<string, unknown> };
type LinkNode = { marks?: LinkMark[]; content?: LinkNode[] };

const isExternalHref = (href: unknown, siteUrl: string): boolean => {
  if (typeof href !== 'string') return false;

  try {
    const { protocol, hostname } = new URL(href);

    return (protocol === 'http:' || protocol === 'https:') && hostname !== new URL(siteUrl).hostname;
  } catch {
    return false;
  }
};

const HONORED_TARGETS = ['_self', '_blank', '_top', '_parent'];

const needsNewTab = (mark: LinkMark, siteUrl: string) =>
  mark.type === 'link' &&
  !HONORED_TARGETS.includes(String(mark.attrs?.target ?? '')) &&
  isExternalHref(mark.attrs?.href, siteUrl);

const resolveNode = (node: LinkNode, siteUrl: string): LinkNode => {
  const content = node.content?.map((child) => resolveNode(child, siteUrl));
  const contentChanged = content?.some((child, index) => child !== node.content?.[index]);
  const marksChanged = node.marks?.some((mark) => needsNewTab(mark, siteUrl));

  if (!contentChanged && !marksChanged) return node;

  const marks = marksChanged
    ? node.marks?.map((mark) =>
        needsNewTab(mark, siteUrl)
          ? { ...mark, attrs: { ...mark.attrs, target: '_blank', rel: 'noopener noreferrer' } }
          : mark,
      )
    : node.marks;

  return { ...node, content, marks };
};

export const resolveLinkTargets = <T>(node: T, siteUrl?: string): T =>
  siteUrl ? (resolveNode(node as LinkNode, siteUrl) as T) : node;
